/**
 * GettingStartedCard — a short checklist on the home page that ticks itself.
 *
 * Every tick is read from real state (skills, saves, applications, the audit
 * queue, demands this person looks after), never from "they clicked the link".
 * While any of that is still loading or failed, the card stays out of the way
 * rather than showing a tick it cannot vouch for. It goes away for good once
 * everything is done, or when dismissed.
 *
 * No requester variant on purpose: MemberHome is one action deep already, and
 * a one-item checklist would only repeat its "Submit a demand" button.
 */

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Button,
  Card,
  Subtitle2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  CheckmarkCircle20Filled,
  Circle20Regular,
  Dismiss16Regular,
} from '@fluentui/react-icons'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useMySkills } from '../hooks/useUserSkills'
import { useMyApplications } from '../hooks/useApplications'
import { useOpportunityEngagement } from '../hooks/useOpportunityEngagement'
import { useUnreviewedLandings } from '../hooks/useUnreviewedLandings'
import { useDemands } from '../hooks/useDemands'
import { dismissChecklist, isChecklistDismissed } from '../lib/onboarding'

export interface ChecklistItem {
  key: string
  label: string
  hint: string
  to: string
  done: boolean
}

const useStyles = makeStyles({
  card: { maxWidth: '560px' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  item: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  done: { color: tokens.colorPaletteGreenForeground1 },
  todo: { color: tokens.colorNeutralForeground3 },
  doneLabel: { color: tokens.colorNeutralForeground3, textDecorationLine: 'line-through' },
})

/** Presentational; exported for tests. `items === null` means "not known yet". */
export function GettingStartedChecklist({ items }: { items: ChecklistItem[] | null }) {
  const styles = useStyles()
  const user = useEffectiveUser()
  const [dismissed, setDismissed] = useState(() => isChecklistDismissed(user?.id))

  if (dismissed || !items || items.every((i) => i.done)) return null

  return (
    <Card className={styles.card} data-testid="getting-started">
      <div className={styles.head}>
        <Subtitle2 as="h2">Getting started</Subtitle2>
        <Tooltip content="Hide" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={() => {
              dismissChecklist(user?.id)
              setDismissed(true)
            }}
            data-testid="getting-started-dismiss"
          />
        </Tooltip>
      </div>
      <ul className={styles.list}>
        {items.map((i) => (
          <li key={i.key} className={styles.item} data-testid={`getting-started-${i.key}`} data-done={i.done}>
            {i.done ? (
              <CheckmarkCircle20Filled className={styles.done} aria-label="Done" />
            ) : (
              <Circle20Regular className={styles.todo} aria-label="To do" />
            )}
            <Tooltip content={i.hint} relationship="description">
              {i.done ? (
                <span className={styles.doneLabel}>{i.label}</span>
              ) : (
                <RouterLink to={i.to}>{i.label}</RouterLink>
              )}
            </Tooltip>
          </li>
        ))}
      </ul>
    </Card>
  )
}

export function TalentGettingStarted() {
  const skills = useMySkills()
  const apps = useMyApplications()
  const engagement = useOpportunityEngagement()

  const known = skills.isSuccess && apps.isSuccess && engagement.isSuccess
  const applied = (apps.data?.total ?? apps.data?.items?.length ?? 0) > 0
  // `?.` on the list too: this card sits on the dashboard, and a malformed
  // answer here must not take the whole page down with it.
  const saved = (engagement.data?.saved_demand_ids?.length ?? 0) > 0

  const items: ChecklistItem[] | null = known
    ? [
        {
          key: 'skills',
          label: 'Add your skills',
          hint: 'Opportunities are matched to them.',
          to: '/profile',
          done: (skills.data?.length ?? 0) > 0,
        },
        {
          key: 'save',
          label: 'Save an opportunity',
          hint: 'Keep the ones you like in one place.',
          to: '/opportunities',
          done: saved || applied,
        },
        {
          key: 'apply',
          label: 'Apply to one',
          hint: 'Track it on this page.',
          to: '/opportunities',
          done: applied,
        },
      ]
    : null

  return <GettingStartedChecklist items={items} />
}

export function CouncilGettingStarted({ focusArea }: { focusArea: string }) {
  const unreviewed = useUnreviewedLandings(focusArea, true)
  const caretaking = useDemands({ mine_caretaker: true, page_size: 1 })

  const known = unreviewed.isSuccess && caretaking.isSuccess
  const items: ChecklistItem[] | null = known
    ? [
        {
          key: 'audit',
          label: 'Clear the Audit queue',
          hint: 'New requests land there. Confirm or route each one.',
          to: '/audit-queue',
          done: unreviewed.data === 0,
        },
        {
          key: 'caretaker',
          label: 'Take on a demand',
          hint: 'Be the caretaker who moves it along.',
          to: '/demands',
          done: (caretaking.data?.total ?? 0) > 0,
        },
      ]
    : null

  return <GettingStartedChecklist items={items} />
}
